import { DateTime } from 'luxon';
import { useMemo } from 'react';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import Tooltip from 'dashboard/components/Tooltip';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import {
  FormLegacyTemplate,
  LegacyTemplateConfigFormData,
} from 'dashboard/forms/FormLegacyTemplate';
import {
  Backdrop,
  ColumnContainer,
  Container,
  HeaderContainer,
  InfoGrid,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Environment } from 'dashboard/models/environment';
import { Template } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

interface DashboardInquiryTemplatesShowViewProps {
  template: Template;
  themes: Theme[];
  users: User[];
  currentEnvironmentType: Environment['type'];
  isLoading: boolean;
  isUpdating: boolean;
  isFormPristine: boolean;
  isFormValid: boolean;
  isSendingTestSupportEmail: boolean;
  onSaveLegacyTemplate: (formValues: LegacyTemplateConfigFormData) => Promise<void>;
  onTestSupportFormEmail: (template: Template) => Promise<void>;
  onInquiryPreview: () => void;
  submit: () => void;
}

const DashboardLegacyTemplatesShowView = ({
  template,
  themes,
  users,
  currentEnvironmentType,
  isLoading,
  isUpdating,
  isFormPristine,
  isFormValid,
  isSendingTestSupportEmail,
  onSaveLegacyTemplate,
  onInquiryPreview,
  onTestSupportFormEmail,
  submit,
}: DashboardInquiryTemplatesShowViewProps) => {
  const [showSaveModal, { prompt: promptSave, confirm: confirmSave, cancel: cancelSave }] =
    useConfirmation();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const initialValues = useMemo<Partial<LegacyTemplateConfigFormData>>(() => {
    if (template == null) {
      return undefined;
    }

    return {
      ...template.attributes,
      'default-theme-id': template.relationships['default-theme']?.data?.id,
    };
  }, [template]);

  const hasTemplateWritePermission = usePermission(Permission.InquiryTemplateWrite);

  if (template == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Template.'
          backRoute='/dashboard/inquiry-templates'
        />
      </Backdrop>
    );
  }

  return (
    <>
      <ModalWrapper isOpen={showSaveModal} handleCloseModal={cancelSave}>
        <ConfirmationModal
          cancelAction={cancelSave}
          confirmAction={confirmSave}
          cancelActionText='Don’t save'
          confirmActionText='Save'
          header='Save changes?'
          confirmButtonType='primary'
        >
          Saving will affect any production inquiries made with this template.
        </ConfirmationModal>
      </ModalWrapper>

      <Backdrop>
        <Container data-test='templates-view'>
          <HeaderContainer>
            <Header>
              <NavBack to='/dashboard/inquiry-templates' dataTest='templates-show__header'>
                All templates
              </NavBack>
              <HStack>
                <Permissioned permission={Permission.InquiryTemplateWrite} fallback={null}>
                  <Button
                    id='templates-view__save-template'
                    data-test='templates-view__save-template'
                    onClick={() => promptSave(submit)}
                    disabled={isLoading || isUpdating || isFormPristine || !isFormValid}
                    isPending={isUpdating}
                    width='auto'
                  >
                    Save changes
                  </Button>
                </Permissioned>
                <Permissioned permission={Permission.InquiryWrite} fallback={null}>
                  {currentEnvironmentType === ApiModel.EnvironmentSandbox ? (
                    <Button
                      id='templates-view__view-template'
                      data-test='templates-view__view-template'
                      onClick={onInquiryPreview}
                      disabled={isLoading || isUpdating || !isFormPristine}
                      width='auto'
                      variant='basic'
                    >
                      Preview inquiry
                    </Button>
                  ) : (
                    <Tooltip
                      placement='bottom'
                      tooltip={
                        <Paragraph style={{ maxWidth: '300px' }}>
                          Preview runs in Sandbox environments. You can switch environments using
                          the menu at the top left of the dashboard.
                        </Paragraph>
                      }
                    >
                      <Button
                        id='templates-view__view-template'
                        data-test='templates-view__view-template'
                        width='auto'
                        variant='basic'
                        disabled={true}
                      >
                        Preview inquiry
                      </Button>
                    </Tooltip>
                  )}
                </Permissioned>
              </HStack>
            </Header>
          </HeaderContainer>
          <ColumnContainer>
            <MainColumn>
              <FormLegacyTemplate
                template={template}
                themes={themes}
                users={users}
                canEdit={hasTemplateWritePermission}
                handleTestSupportFormEmail={onTestSupportFormEmail}
                isSendingTestSupportEmail={isSendingTestSupportEmail}
                isFormPristine={isFormPristine}
                initialValues={initialValues}
                onSubmit={onSaveLegacyTemplate}
              />
            </MainColumn>
            <RightColumn>
              <InquiryTemplateInfo template={template} />
            </RightColumn>
          </ColumnContainer>
        </Container>
      </Backdrop>
    </>
  );
};

interface InquiryTemplateInfoProps {
  template: Template;
}

const InquiryTemplateInfo = ({ template }: InquiryTemplateInfoProps) => {
  return (
    <Card>
      <Card.Body>
        <Card.Title>Info</Card.Title>
        <InfoGrid>
          <AttributeItem
            title='Template ID'
            value={template.id}
            isRedacted={false}
            placeholder=''
            wordBreak='break-all'
            dataTest='inquiry-template-info__inquiry-template-id'
          />
          <AttributeItem
            title='Last updated at'
            value={DateTime.fromISO(template.attributes['updated-at']).toFormat(
              DATE_AND_TIME_FORMAT,
            )}
            isRedacted={false}
            placeholder=''
            wordBreak='break-all'
            dataTest='inquiry-template-info__updated-at'
          />
        </InfoGrid>
      </Card.Body>
    </Card>
  );
};

export default DashboardLegacyTemplatesShowView;
